import * as React from "react"
import { Label } from "./label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"

interface EligibilityField {
  key: string
  label: string
  type: 'text' | 'array' | 'number' | 'date'
  category: string
}

const ELIGIBILITY_FIELDS: EligibilityField[] = [
  // Basic Information
  { key: 'date_of_birth', label: 'Date of Birth', type: 'date', category: 'basic' },
  { key: 'gender', label: 'Gender', type: 'text', category: 'basic' },
  
  // Physical Metrics  
  { key: 'height_feet', label: 'Height (Feet)', type: 'text', category: 'physical' },
  { key: 'height_inches', label: 'Height (Inches)', type: 'text', category: 'physical' },
  { key: 'weight', label: 'Weight', type: 'text', category: 'physical' },
  { key: 'bmi', label: 'BMI', type: 'number', category: 'physical' },
  { key: 'activity_level', label: 'Activity Level', type: 'text', category: 'physical' },
  
  // Goals & Preferences
  { key: 'weight_loss_goals', label: 'Weight Loss Goals', type: 'text', category: 'goals' },
  { key: 'motivations', label: 'Motivations', type: 'array', category: 'goals' },
  { key: 'medication_preference', label: 'Medication Preference', type: 'text', category: 'goals' },
  
  // Medical History
  { key: 'diagnosed_conditions', label: 'Diagnosed Conditions', type: 'array', category: 'medical' },
  { key: 'chronic_diseases', label: 'Chronic Diseases', type: 'array', category: 'medical' },
  { key: 'family_medical_history', label: 'Family Medical History', type: 'array', category: 'medical' },
  { key: 'family_health', label: 'Family Health', type: 'array', category: 'medical' },
  { key: 'medication_history', label: 'Medication History', type: 'array', category: 'medical' },
  { key: 'procedures', label: 'Procedures', type: 'array', category: 'medical' },
  { key: 'supplements', label: 'Supplements', type: 'array', category: 'medical' },
  { key: 'allergies', label: 'Allergies', type: 'array', category: 'medical' },
  
  // Mental Health
  { key: 'eating_disorders', label: 'Eating Disorders', type: 'array', category: 'mental' },
  { key: 'mental_health', label: 'Mental Health', type: 'array', category: 'mental' },
  { key: 'self_harm_screening', label: 'Self Harm Screening', type: 'array', category: 'mental' },
  
  // Lifestyle
  { key: 'drinking', label: 'Drinking', type: 'text', category: 'lifestyle' },
  { key: 'drugs', label: 'Drugs', type: 'array', category: 'lifestyle' },
  { key: 'smoking', label: 'Smoking', type: 'text', category: 'lifestyle' },
  
  // Health Metrics
  { key: 'heart_rate', label: 'Heart Rate', type: 'text', category: 'health' },
  { key: 'gastrointestinal', label: 'Gastrointestinal', type: 'array', category: 'health' },
  
  // Side Effects & Dosing
  { key: 'side_effects', label: 'Side Effects', type: 'array', category: 'dosing' },
  { key: 'side_effect_guidance', label: 'Side Effect Guidance', type: 'array', category: 'dosing' },
  { key: 'gastrointestinal_dosing', label: 'Gastrointestinal Dosing', type: 'text', category: 'dosing' },
  { key: 'energy_dosing', label: 'Energy Dosing', type: 'text', category: 'dosing' },
  { key: 'muscle_loss_dosing', label: 'Muscle Loss Dosing', type: 'text', category: 'dosing' },
  
  // Program & Challenges
  { key: 'challenges', label: 'Challenges', type: 'array', category: 'program' },
  { key: 'challenges_elaborate', label: 'Challenge Details', type: 'text', category: 'program' },
  { key: 'program_adherence', label: 'Program Adherence', type: 'array', category: 'program' },
  { key: 'program_consistency', label: 'Program Consistency', type: 'array', category: 'program' },
  
  // Additional
  { key: 'additional_info', label: 'Additional Information', type: 'text', category: 'additional' }
]

const CATEGORY_LABELS = {
  basic: 'Basic Information',
  physical: 'Physical Metrics', 
  goals: 'Goals & Preferences',
  medical: 'Medical History',
  mental: 'Mental Health',
  lifestyle: 'Lifestyle',
  health: 'Health Metrics',
  dosing: 'Side Effects & Dosing',
  program: 'Program & Challenges',
  additional: 'Additional Information'
}

interface EligibilitySectionProps {
  patient: any
}

function formatValue(value: any, type: EligibilityField['type']): string {
  if (!value) return ''
  
  switch (type) {
    case 'date':
      return new Date(value).toLocaleDateString()
    case 'array':
      return Array.isArray(value) ? value.join(', ') : value.toString()
    case 'number':
      return value.toString()
    case 'text':
    default:
      return value.toString()
  }
}

function formatHeight(feet?: string, inches?: string): string {
  if (!feet && !inches) return ''
  return `${feet || 0}'${inches || 0}"`
}

export function EligibilitySection({ patient }: EligibilitySectionProps) {
  const [selectedCategory, setSelectedCategory] = React.useState<string>('all')
  
  console.log('🔍 ELIGIBILITY SECTION: Patient data keys:', Object.keys(patient || {}))
  console.log('🔍 ELIGIBILITY SECTION: Complete patient object:', patient)
  
  // Check ALL intake fields that are being fetched from the database
  const intakeFields = {
    date_of_birth: patient?.date_of_birth,
    gender: patient?.gender,
    weight_loss_goals: patient?.weight_loss_goals,
    motivations: patient?.motivations,
    height_feet: patient?.height_feet,
    height_inches: patient?.height_inches,
    weight: patient?.weight,
    bmi: patient?.bmi,
    activity_level: patient?.activity_level,
    medication_preference: patient?.medication_preference,
    diagnosed_conditions: patient?.diagnosed_conditions,
    chronic_diseases: patient?.chronic_diseases,
    family_medical_history: patient?.family_medical_history,
    family_health: patient?.family_health,
    allergies: patient?.allergies,
    medication_history: patient?.medication_history,
    procedures: patient?.procedures,
    supplements: patient?.supplements,
    eating_disorders: patient?.eating_disorders,
    mental_health: patient?.mental_health,
    self_harm_screening: patient?.self_harm_screening,
    drinking: patient?.drinking,
    drugs: patient?.drugs,
    smoking: patient?.smoking,
    heart_rate: patient?.heart_rate,
    gastrointestinal: patient?.gastrointestinal,
    side_effects: patient?.side_effects,
    side_effect_guidance: patient?.side_effect_guidance,
    gastrointestinal_dosing: patient?.gastrointestinal_dosing,
    energy_dosing: patient?.energy_dosing,
    muscle_loss_dosing: patient?.muscle_loss_dosing,
    challenges: patient?.challenges,
    challenges_elaborate: patient?.challenges_elaborate,
    program_adherence: patient?.program_adherence,
    program_consistency: patient?.program_consistency,
    additional_info: patient?.additional_info
  }
  console.log('🔍 ELIGIBILITY SECTION: Intake fields status:', intakeFields)
  
  // Count how many intake fields have data (accounting for different data types)
  const fieldsWithData = Object.entries(intakeFields).filter(([key, value]) => {
    console.log(`🔍 FIELD VALIDATION: ${key} =`, value, `(type: ${typeof value})`)
    
    if (value === undefined || value === null || value === '') {
      console.log(`❌ FIELD VALIDATION: ${key} is null/undefined/empty`)
      return false
    }
    
    // Handle arrays - only count if they have actual elements and not just ["none"] or ["no"]
    if (Array.isArray(value)) {
      const hasRealData = value.length > 0 && !value.every(item => 
        item === 'none' || item === 'no' || item === '' || item === null
      )
      console.log(`🔍 FIELD VALIDATION: ${key} array has real data:`, hasRealData, value)
      return hasRealData
    }
    
    // Handle numbers (including string numbers) - zero is valid data
    if (typeof value === 'number') {
      const isValid = !isNaN(value)
      console.log(`🔍 FIELD VALIDATION: ${key} number is valid:`, isValid)
      return isValid
    }
    
    // Handle strings - exclude whitespace-only strings and negative responses
    if (typeof value === 'string') {
      const trimmed = value.trim()
      const hasData = trimmed.length > 0 && 
        trimmed !== 'none' && 
        trimmed !== 'no' && 
        trimmed !== 'never' &&
        trimmed !== 'dont-know'
      console.log(`🔍 FIELD VALIDATION: ${key} string has data:`, hasData, `"${trimmed}"`)
      return hasData
    }
    
    // For other types (booleans, objects), check truthiness
    const hasData = Boolean(value)
    console.log(`🔍 FIELD VALIDATION: ${key} other type has data:`, hasData)
    return hasData
  })
  console.log('🔍 ELIGIBILITY SECTION: Fields with data:', fieldsWithData.length, 'out of', Object.keys(intakeFields).length)
  
  // Use actual patient data only - no mock data in healthcare applications
  const testPatient = patient
  
  // Filter fields by category
  const fieldsToShow = selectedCategory === 'all' 
    ? ELIGIBILITY_FIELDS 
    : ELIGIBILITY_FIELDS.filter(field => field.category === selectedCategory)
  
  // Group fields by category
  const fieldsByCategory = fieldsToShow.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = []
    }
    acc[field.category].push(field)
    return acc
  }, {} as Record<string, EligibilityField[]>)
  
  return (
    <div className="space-y-4">
      <div className="bg-muted/50 rounded-xl">
        {/* Category Selector */}
        <div className="p-4 pb-0">
          <Label className="text-sm font-medium">Category of Care</Label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full mt-1">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="basic">Basic Information</SelectItem>
              <SelectItem value="physical">Physical Metrics</SelectItem>
              <SelectItem value="goals">Goals & Preferences</SelectItem>
              <SelectItem value="medical">Medical History</SelectItem>
              <SelectItem value="mental">Mental Health</SelectItem>
              <SelectItem value="lifestyle">Lifestyle</SelectItem>
              <SelectItem value="health">Health Metrics</SelectItem>
              <SelectItem value="dosing">Side Effects & Dosing</SelectItem>
              <SelectItem value="program">Program & Challenges</SelectItem>
              <SelectItem value="additional">Additional Information</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Eligibility Fields */}
        <div className="grid gap-4 p-4">
          {Object.entries(fieldsByCategory).map(([category, fields]) => {
            // Filter out fields that have no data (using same logic as field counting)
            const fieldsWithData = fields.filter(field => {
              const value = testPatient?.[field.key]
              
              if (value === undefined || value === null || value === '') {
                return false
              }
              
              // Handle arrays - only show if they have actual elements and not just ["none"] or ["no"]
              if (Array.isArray(value)) {
                return value.length > 0 && !value.every(item => 
                  item === 'none' || item === 'no' || item === '' || item === null
                )
              }
              
              // Handle numbers - zero is valid data
              if (typeof value === 'number') {
                return !isNaN(value)
              }
              
              // Handle strings - exclude whitespace-only strings and negative responses
              if (typeof value === 'string') {
                const trimmed = value.trim()
                return trimmed.length > 0 && 
                  trimmed !== 'none' && 
                  trimmed !== 'no' && 
                  trimmed !== 'never' &&
                  trimmed !== 'dont-know'
              }
              
              // For other types (booleans, objects), check truthiness
              return Boolean(value)
            })
            
            // Don't render empty categories
            if (fieldsWithData.length === 0) return null
            
            return (
              <div key={category} className="space-y-3">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  {CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]}
                </h4>
                
                {fieldsWithData.map(field => {
                  const value = testPatient?.[field.key]
                  
                  // Special handling for height
                  if (field.key === 'height_feet' || field.key === 'height_inches') {
                    // Only show height once when we hit height_feet
                    if (field.key === 'height_inches') return null
                    
                    const heightDisplay = formatHeight(testPatient?.height_feet, testPatient?.height_inches)
                    if (!heightDisplay) return null
                    
                    return (
                      <div key="height" className="flex justify-between items-center py-2 border-b border-border/50">
                        <span className="text-sm font-medium">Height</span>
                        <span className="text-sm text-muted-foreground">{heightDisplay}</span>
                      </div>
                    )
                  }
                  
                  // Special handling for weight
                  if (field.key === 'weight' && value) {
                    return (
                      <div key={field.key} className="flex justify-between items-center py-2 border-b border-border/50">
                        <span className="text-sm font-medium">{field.label}</span>
                        <span className="text-sm text-muted-foreground">{value} lbs</span>
                      </div>
                    )
                  }
                  
                  // Special handling for heart rate
                  if (field.key === 'heart_rate' && value) {
                    return (
                      <div key={field.key} className="flex justify-between items-center py-2 border-b border-border/50">
                        <span className="text-sm font-medium">{field.label}</span>
                        <span className="text-sm text-muted-foreground">{value} bpm</span>
                      </div>
                    )
                  }
                  
                  // Special handling for additional_info (text block)
                  if (field.key === 'additional_info' && value) {
                    return (
                      <div key={field.key} className="space-y-2">
                        <h5 className="text-sm font-medium">{field.label}</h5>
                        <div className="p-3 bg-muted rounded-md">
                          <p className="text-sm text-muted-foreground">{value}</p>
                        </div>
                      </div>
                    )
                  }
                  
                  // Standard field display
                  const formattedValue = formatValue(value, field.type)
                  if (!formattedValue) return null
                  
                  return (
                    <div key={field.key} className="flex justify-between items-start py-2 border-b border-border/50">
                      <span className="text-sm font-medium">{field.label}</span>
                      <span className="text-sm text-muted-foreground text-right max-w-xs">
                        {field.type === 'text' && formattedValue.length > 50 ? (
                          <span className="break-words">{formattedValue}</span>
                        ) : (
                          formattedValue
                        )}
                      </span>
                    </div>
                  )
                })}
              </div>
            )
          })}
          
          {/* No data message */}
          {Object.keys(fieldsByCategory).length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">No intake data available for the selected category.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}